import { Download } from "@mui/icons-material";
import { Box, CircularProgress, IconButton, Typography, alpha } from "@mui/material";
import { SingleMessageType } from "DataTypes/Chat.type";
import { FC, useEffect, useState } from "react";

export interface AttachmentCompType {
  message: SingleMessageType;
  setSelectedDocument: ({
    attachmentUrl,
    docType,
  }: {
    attachmentUrl: string;
    docType: "image" | "video";
  }) => void;
  downloadImage: (attachmentUrl: string, fileName: string) => void;
}

const AttachmentComponent: FC<AttachmentCompType> = (
  props: AttachmentCompType
) => {
  const { message, setSelectedDocument, downloadImage } = props;
  const isHeic = Boolean(message.fileName?.match(/\.(heic|heif)$/i) || message.attachmentUrl?.match(/\.(heic|heif)$/i));
  const isImage = Boolean(message.fileName?.match(/\.(jpg|jpeg|png|gif|heic|heif|webp)$/i) || message.attachmentUrl?.match(/\.(jpg|jpeg|png|gif|heic|heif|webp)$/i));

  const [displayUrl, setDisplayUrl] = useState<string | null>(isHeic ? null : message.attachmentUrl);
  const [isConverting, setIsConverting] = useState<boolean>(isHeic);

  useEffect(() => {
    let active = true;
    let createdObjectUrl: string | null = null;

    if (isHeic && message.attachmentUrl) {
      setIsConverting(true);
      fetch(message.attachmentUrl)
        .then((res) => {
          if (!res.ok) throw new Error("Failed to fetch HEIC image");
          return res.blob();
        })
        .then(async (blob) => {
          const heic2any = (await import("heic2any")).default;
          const converted = await heic2any({
            blob,
            toType: "image/jpeg",
            quality: 0.85,
          });
          const convertedBlob = Array.isArray(converted) ? converted[0] : converted;
          if (active) {
            createdObjectUrl = URL.createObjectURL(convertedBlob);
            setDisplayUrl(createdObjectUrl);
            setIsConverting(false);
          }
        })
        .catch((err) => {
          console.error("HEIC conversion failed:", err);
          if (active) {
            setDisplayUrl(message.attachmentUrl);
            setIsConverting(false);
          }
        });
    } else {
      setDisplayUrl(message.attachmentUrl);
      setIsConverting(false);
    }

    return () => {
      active = false;
      if (createdObjectUrl) {
        URL.revokeObjectURL(createdObjectUrl);
      }
    };
  }, [message.attachmentUrl, isHeic]);

  return (
    <Box sx={{ mt: 1 }}>
      {isImage ? (
        <Box sx={{
          position: 'relative',
          width: 'fit-content',
          borderRadius: "12px",
          overflow: 'hidden',
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
          border: "1px solid rgba(0, 0, 0, 0.05)"
        }}>
          {isConverting ? (
            <Box sx={{
              width: "200px",
              height: "150px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justify: "center",
              bgcolor: "#f5f5f5",
              gap: 1
            }}>
              <CircularProgress size={24} sx={{ color: "#1a237e" }} />
              <Typography variant="caption" sx={{ color: "#1a237e", fontWeight: 600 }}>
                Loading Image...
              </Typography>
            </Box>
          ) : (
            <>
              <img
                src={displayUrl || message.attachmentUrl}
                alt={message.fileName}
                onClick={() =>
                  setSelectedDocument({
                    attachmentUrl: displayUrl || message.attachmentUrl,
                    docType: "image",
                  })
                }
                style={{
                  maxWidth: "240px",
                  maxHeight: "240px",
                  display: "block",
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease',
                }}
              />
              <IconButton
                onClick={() =>
                  downloadImage(
                    message.attachmentUrl,
                    message.fileName || "image.jpg"
                  )
                }
                size="small"
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  bgcolor: 'rgba(255,255,255,0.9)',
                  backdropFilter: 'blur(4px)',
                  color: '#1a237e',
                  '&:hover': { bgcolor: '#fff' }
                }}
              >
                <Download fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>
      ) : message.fileName?.match(/\.(mp4|webm|mov)$/i) ? (
        <Box sx={{
          position: 'relative',
          width: 'fit-content',
          borderRadius: "12px",
          overflow: 'hidden',
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
          border: "1px solid rgba(0, 0, 0, 0.05)"
        }}>
          <video
            controls
            controlsList="nodownload"
            width="260"
            style={{ display: 'block' }}
            onClick={() =>
              setSelectedDocument({
                attachmentUrl: message.attachmentUrl,
                docType: "video",
              })
            }
          >
            <source src={message.attachmentUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <IconButton
            onClick={() =>
              downloadImage(
                message.attachmentUrl,
                message.fileName || "video.mp4"
              )
            }
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(4px)',
              color: '#1a237e',
              '&:hover': { bgcolor: '#fff' }
            }}
          >
            <Download fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 1.5,
            background: '#fff',
            borderRadius: "12px",
            border: '1px solid rgba(26, 35, 126, 0.12)',
            textDecoration: 'none',
            transition: 'all 0.2s ease',
            '&:hover': {
              background: alpha("#1a237e", 0.02),
              borderColor: '#1a237e',
              transform: 'translateY(-1px)'
            }
          }}
          component="a"
          href={message.attachmentUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Box sx={{
            p: 1,
            bgcolor: alpha("#1a237e", 0.06),
            borderRadius: "10px",
            mr: 1.5,
            display: 'flex'
          }}>
            <Download fontSize="small" sx={{ color: '#1a237e' }} />
          </Box>
          <Box sx={{ overflow: 'hidden' }}>
            <Typography
              sx={{
                color: "#1a237e",
                fontSize: '0.85rem',
                fontWeight: 700,
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden'
              }}
            >
              {message.fileName || "Attached File"}
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.primary', opacity: 0.6, fontWeight: 700 }}>
              VIEW DOCUMENT
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AttachmentComponent;
